import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { OuigooLocationPicker } from '@ouigoo/location-picker/react';
import { countryOptions, prefixByIso, isoByPrefix, CloseIcon, Spinner } from '../components/catalogue/catalogueHelpers';
import CatalogueView from '../components/catalogue/CatalogueView';
import ProductView from '../components/catalogue/ProductView';
import CheckoutView from '../components/catalogue/CheckoutView';
import SuccessView from '../components/catalogue/SuccessView';
import { WhatsAppCheckoutView, WhatsAppSentView, WhatsAppExpiredView } from '../components/catalogue/WhatsAppViews';
import { initMerchantPixel, trackInitiateCheckout, trackPurchase } from '../utils/meta-pixel';

const API = process.env.REACT_APP_BUSINESS_API_ENDPOINT;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

// Device-local memory of the customer's last delivery info, so a returning
// customer doesn't retype name / phone / address. Same key as the public order
// form, so both flows share the customer's saved info on this browser.
const PREFILL_STORAGE_KEY = 'ouigoo_order_prefill';

const loadPrefill = () => {
  try {
    const raw = localStorage.getItem(PREFILL_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
};

const consumeInitialData = () => {
  if (typeof window === 'undefined' || !window.__INITIAL_DATA__) return null;
  const data = window.__INITIAL_DATA__;
  delete window.__INITIAL_DATA__;
  return data;
};

const CataloguePage = () => {
  const { slug, productId } = useParams();
  // Opened from the WhatsApp bot (?wa=<token>): the cart goes back to the
  // conversation instead of becoming an order here (see WhatsAppViews).
  const location = useLocation();
  const waToken = new URLSearchParams(location.search).get('wa');
  const [whatsapp, setWhatsapp] = useState({ url: null, expired: false });
  const initialDataRef = useRef(consumeInitialData());
  const initialData = initialDataRef.current;

  // ─── Catalogue state ───────────────────────────────────────────────────────
  const [catalogue, setCatalogue] = useState(initialData?.catalogue || null);
  const [products, setProducts] = useState(initialData?.catalogue?.products?.data || []);
  const [isLoading, setIsLoading] = useState(!initialData);
  const [error, setError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(() => {
    const tp = initialData?.catalogue?.products?.totalPages ?? 1;
    return 0 < tp - 1;
  });

  // ─── Cart ──────────────────────────────────────────────────────────────────
  const [cart, setCart] = useState([]);
  const [buyNowItems, setBuyNowItems] = useState(null);

  // ─── View ──────────────────────────────────────────────────────────────────
  const [view, setView] = useState(initialData?.product ? 'product' : 'catalogue');
  const [selectedProduct, setSelectedProduct] = useState(initialData?.product || null);
  const [orderResult, setOrderResult] = useState(null);
  const [canGoBack, setCanGoBack] = useState(false);

  // ─── Checkout form ─────────────────────────────────────────────────────────
  const [selectedCountryCode, setSelectedCountryCode] = useState(() => loadPrefill()?.countryCode || '+229');
  const [checkoutForm, setCheckoutForm] = useState(() => {
    const saved = loadPrefill();
    return {
      customer_name: saved?.customer_name || '',
      phone: saved?.phone || '',
      address: saved?.address || '',
      lat: saved?.lat ?? null,
      lng: saved?.lng ?? null,
      instructions: saved?.instructions || '',
    };
  });
  const [hasPrefill, setHasPrefill] = useState(() => !!loadPrefill());
  const [checkoutErrors, setCheckoutErrors] = useState({});
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [estimation, setEstimation] = useState(null);
  const [isEstimating, setIsEstimating] = useState(false);

  // ─── Product state ─────────────────────────────────────────────────────────
  const [productQuantity, setProductQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // ─── Search ────────────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const skipNextCheckoutPopRef = useRef(false);
  const buyNowItemsRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => { buyNowItemsRef.current = buyNowItems; }, [buyNowItems]);

  // ─── Meta Pixel du marchand ────────────────────────────────────────────────
  // Coexiste avec le pixel OuiGoo (déjà initié globalement dans App.js) : les deux
  // reçoivent les mêmes événements (InitiateCheckout / Purchase) de cette page.
  useEffect(() => {
    if (catalogue?.settings?.pixelId) initMerchantPixel(catalogue.settings.pixelId);
  }, [catalogue?.settings?.pixelId]);

  // Étape "checkout" atteinte : signal de tunnel pour les deux pixels.
  useEffect(() => {
    if (view === 'checkout') trackInitiateCheckout({ value: checkoutTotal });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // ─── Geo-detect phone prefix ───────────────────────────────────────────────
  // Skip when a saved prefix was restored from a previous order — the
  // customer's own choice wins.
  useEffect(() => {
    if (loadPrefill()?.countryCode) return;
    fetch('https://ipapi.co/json/')
      .then(r => r.json())
      .then(data => {
        const prefix = data?.country_code && prefixByIso[data.country_code];
        if (prefix) setSelectedCountryCode(prefix);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearPrefill = () => {
    try { localStorage.removeItem(PREFILL_STORAGE_KEY); } catch (err) { /* ignore */ }
    setCheckoutForm({ customer_name: '', phone: '', address: '', lat: null, lng: null, instructions: '' });
    setSelectedCountryCode('+229');
    setHasPrefill(false);
  };

  // ─── Data fetching ─────────────────────────────────────────────────────────
  const loadCatalogue = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API}/v1/public/catalogue/${slug}?page=0&pageSize=20`);
      if (!res.ok) { setError('Catalogue introuvable.'); return; }
      const data = await res.json();
      setCatalogue(data);
      setProducts(data.products?.data || []);
      const tp = data.products?.totalPages ?? 1;
      setHasMore(0 < tp - 1);
      setCurrentPage(0);
    } catch {
      setError('Erreur lors du chargement.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadProductDirect = async () => {
    setIsLoading(true);
    try {
      const [settingsRes, productRes] = await Promise.all([
        fetch(`${API}/v1/public/catalogue/${slug}/settings`),
        fetch(`${API}/v1/public/catalogue/${slug}/p/${productId}`),
      ]);
      if (!settingsRes.ok || !productRes.ok) { setError('Produit introuvable.'); return; }
      const [storeSettings, product] = await Promise.all([settingsRes.json(), productRes.json()]);
      setCatalogue(storeSettings);
      setSelectedProduct(product);
      setView('product');
    } catch {
      setError('Erreur lors du chargement.');
    } finally {
      setIsLoading(false);
    }
  };

  const skipNextCatalogueLoadRef = useRef(Boolean(initialData?.catalogue));
  useEffect(() => {
    if (skipNextCatalogueLoadRef.current) {
      skipNextCatalogueLoadRef.current = false;
      return;
    }
    if (productId) {
      loadProductDirect();
    } else {
      loadCatalogue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // ─── WhatsApp: the cart already started in the conversation ───────────────
  useEffect(() => {
    if (!waToken) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/v1/public/whatsapp/cart?token=${encodeURIComponent(waToken)}`);
        if (res.status === 410) {
          if (!cancelled) setWhatsapp(prev => ({ ...prev, expired: true }));
          return;
        }
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setWhatsapp(prev => ({ ...prev, url: data.whatsapp_url || null }));
        const lines = await Promise.all((data.items || []).map(async (line) => {
          const r = await fetch(`${API}/v1/public/catalogue/${slug}/p/${line.product_id}`);
          if (!r.ok) return null;
          const product = await r.json();
          const variant = line.variant ? (product.variants || []).find(v => v.name === line.variant) || null : null;
          const options = (line.options || []).map(o => {
            const choice = (product.options || []).find(g => g.label === o.group)?.choices?.find(c => c.name === o.name);
            return choice ? { groupLabel: o.group, name: o.name, price: choice.price ?? 0, qty: o.qty ?? 1 } : null;
          }).filter(Boolean);
          return { product, quantity: line.quantity, variant, options };
        }));
        if (!cancelled) setCart(lines.filter(Boolean));
      } catch {
        // The catalogue still works, with an empty cart.
      }
    })();
    return () => { cancelled = true; };
  }, [waToken, slug]);

  // ─── Open product from URL param ───────────────────────────────────────────
  useEffect(() => {
    if (!productId || isLoading) return;
    if (selectedProduct && (String(selectedProduct.id) === String(productId) || selectedProduct.slug === productId)) {
      return;
    }
    const found = products.find(p => String(p.id) === String(productId) || p.slug === productId);
    if (found) { setSelectedProduct(found); setView('product'); return; }
    fetch(`${API}/v1/public/catalogue/${slug}/p/${productId}`)
      .then(r => r.ok ? r.json() : null)
      .then(p => { if (p) { setSelectedProduct(p); setView('product'); } })
      .catch(() => {});
  }, [productId, isLoading, products, slug, selectedProduct, view]);

  // ─── Debounced search ──────────────────────────────────────────────────────
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) { setSearchResults(null); return; }
    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/v1/public/catalogue/${slug}/products?search=${encodeURIComponent(q)}&page=0&pageSize=50`);
        if (res.ok) { const data = await res.json(); setSearchResults(data.data || []); }
      } catch { } finally { setIsSearching(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery, slug]);

  // ─── Sync URL with view ────────────────────────────────────────────────────
  useEffect(() => {
    if (view === 'product' && selectedProduct) {
      const ref = selectedProduct.slug || selectedProduct.id;
      const target = `/c/${slug}/p/${ref}`;
      if (window.location.pathname !== target) window.history.pushState({ productRef: ref }, '', target);
    } else if (view === 'catalogue') {
      const target = `/c/${slug}`;
      if (window.location.pathname !== target) window.history.pushState({}, '', target);
    }
  }, [view, selectedProduct, slug]);

  // ─── Sync meta tags ────────────────────────────────────────────────────────
  useEffect(() => {
    const businessName = catalogue?.businessName;
    const businessDesc = catalogue?.settings?.description;
    let title, metaDesc, image, ogType;
    if (view === 'product' && selectedProduct) {
      title = businessName ? `${selectedProduct.name} - ${businessName}` : selectedProduct.name;
      metaDesc = selectedProduct.description || (businessName ? `Découvrez ${selectedProduct.name} sur ${businessName}.` : null);
      image = selectedProduct.images?.[0]?.url || catalogue?.settings?.logoUrl || null;
      ogType = 'product';
    } else if (businessName) {
      title = `${businessName} - Catalogue`;
      metaDesc = businessDesc || `Découvrez le catalogue de ${businessName}.`;
      image = catalogue?.settings?.logoUrl || null;
      ogType = 'website';
    }
    if (title) document.title = title;
    const setMeta = (selector, attr, key, value) => {
      if (!value) return;
      let tag = document.querySelector(selector);
      if (!tag) { tag = document.createElement('meta'); tag.setAttribute(attr, key); document.head.appendChild(tag); }
      tag.setAttribute('content', value);
    };
    setMeta('meta[name="description"]', 'name', 'description', metaDesc);
    setMeta('meta[property="og:title"]', 'property', 'og:title', title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', metaDesc);
    setMeta('meta[property="og:image"]', 'property', 'og:image', image);
    setMeta('meta[property="og:url"]', 'property', 'og:url', window.location.href);
    setMeta('meta[property="og:type"]', 'property', 'og:type', ogType);
    setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', businessName);
    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', image ? 'summary_large_image' : 'summary');
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', metaDesc);
    setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', image);
  }, [view, selectedProduct, catalogue]);

  // ─── Browser back button ───────────────────────────────────────────────────
  useEffect(() => {
    if (view === 'checkout') window.history.pushState({ checkout: true }, '');
    const handlePop = (e) => {
      if (view === 'checkout') {
        if (skipNextCheckoutPopRef.current) { skipNextCheckoutPopRef.current = false; return; }
        if (e.state?.checkout) return;
        const hadBuyNow = buyNowItemsRef.current !== null;
        setBuyNowItems(null);
        setView(hadBuyNow ? 'product' : 'catalogue');
      } else if (view === 'product') {
        if (products.length === 0) loadCatalogue();
        setView('catalogue');
        setSelectedProduct(null);
        setProductQuantity(1);
        setSelectedVariant(null);
        setSelectedOptions([]);
        setActiveImageIndex(0);
      }
    };
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [view]);

  // ─── Location picker back button ───────────────────────────────────────────
  useEffect(() => {
    if (!showLocationPicker) return;
    window.history.pushState({ locationPicker: true }, '');
    const handlePop = () => setShowLocationPicker(false);
    window.addEventListener('popstate', handlePop);
    return () => window.removeEventListener('popstate', handlePop);
  }, [showLocationPicker]);

  const closePicker = () => {
    if (window.history.state?.locationPicker) {
      if (view === 'checkout') skipNextCheckoutPopRef.current = true;
      window.history.back();
    } else {
      setShowLocationPicker(false);
    }
  };

  // ─── Infinite scroll ───────────────────────────────────────────────────────
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) loadMore(); },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMore, currentPage]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const next = currentPage + 1;
      const res = await fetch(`${API}/v1/public/catalogue/${slug}/products?page=${next}&pageSize=20`);
      if (res.ok) {
        const data = await res.json();
        setProducts(prev => [...prev, ...(data.data || [])]);
        setCurrentPage(next);
        setHasMore(next < (data.totalPages ?? 1) - 1);
      }
    } catch { } finally { setLoadingMore(false); }
  };

  // ─── Cart helpers ──────────────────────────────────────────────────────────
  // Per-item unit price = base (variant or product) + sum of selected options.
  const unitPrice = (item) => {
    const base = item.variant?.price ?? item.product.price ?? 0;
    const optionsTotal = (item.options ?? []).reduce((s, o) => s + (o.price ?? 0) * (o.qty ?? 1), 0);
    return base + optionsTotal;
  };

  const checkoutItems = buyNowItems ?? cart;
  const cartTotal = cart.reduce((sum, item) => sum + unitPrice(item) * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const checkoutTotal = checkoutItems.reduce((sum, item) => sum + unitPrice(item) * item.quantity, 0);
  const checkoutCount = checkoutItems.reduce((sum, item) => sum + item.quantity, 0);
  const getCartItem = (id) => cart.find(item => item.product.id === id);

  // One more of the product. Its line keeps the variant and options already
  // chosen, e.g. those of a cart brought back from WhatsApp.
  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (existing) return prev.map(item => item.product.id === product.id ? { ...item, quantity: item.quantity + 1 } : item);
      return [...prev, { product, quantity: 1, variant: null, options: [] }];
    });
  };

  // Also changes a "buy now" line, which the WhatsApp cart lets you adjust.
  const changeQuantity = (productId, delta) => {
    const update = (items) => items
      .map(item => item.product.id === productId ? { ...item, quantity: Math.min(99, item.quantity + delta) } : item)
      .filter(item => item.quantity > 0);
    if (buyNowItems) setBuyNowItems(update(buyNowItems));
    else setCart(prev => update(prev));
  };

  // One cart line as the order and WhatsApp endpoints expect it.
  const toOrderItem = (item) => ({
    product_id: item.product.id,
    quantity: item.quantity,
    ...(item.variant ? { variant: item.variant.name } : {}),
    ...((item.options ?? []).length > 0
      ? {
          options: item.options.map(o => ({
            group: o.groupLabel,
            name: o.name,
            ...((o.qty ?? 1) > 1 ? { qty: o.qty } : {}),
          })),
        }
      : {}),
  });

  const removeFromCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product.id === product.id);
      if (!existing) return prev;
      if (existing.quantity <= 1) return prev.filter(item => item.product.id !== product.id);
      return prev.map(item => item.product.id === product.id ? { ...item, quantity: item.quantity - 1 } : item);
    });
  };

  // ─── Location select ───────────────────────────────────────────────────────
  const handleLocationSelect = async (result) => {
    const dlat = result.location?.lat;
    const dlng = result.location?.lng;
    setCheckoutForm(prev => ({ ...prev, address: result.address, lat: dlat, lng: dlng }));
    if (checkoutErrors.address) setCheckoutErrors(prev => ({ ...prev, address: '' }));
    closePicker();

    const s = catalogue?.settings;
    const singleProduct = checkoutItems.length === 1 ? checkoutItems[0].product : null;
    const plat = (singleProduct?.pickupLat) ?? s?.pickupLat;
    const plng = (singleProduct?.pickupLng) ?? s?.pickupLng;
    if (s?.estimationEnabled && plat && plng && dlat && dlng) {
      setEstimation(null);
      setIsEstimating(true);
      try {
        const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/v1/public/tariff/simulate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            deliveryPoints: [{ referenceId: result.address, plat, plng, dlat, dlng }],
            rideTypeId: 1,
            productTypeId: 1,
          }),
        });
        if (res.ok) {
          const data = await res.json();
          setEstimation({ tariff: data.grandTotalTariff, distance: data.grandTotalDistance, duration: data.grandTotalDuration });
        }
      } catch (e) {
        console.error('Estimation error:', e);
      } finally {
        setIsEstimating(false);
      }
    }
  };

  // ─── Submit order ──────────────────────────────────────────────────────────
  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!checkoutForm.customer_name.trim()) newErrors.customer_name = 'Veuillez saisir votre nom';
    const phoneTrim = checkoutForm.phone.trim().replace(/\s+/g, '');
    if (!phoneTrim) newErrors.phone = 'Veuillez saisir votre numéro';
    else if (!/^[0-9]{6,15}$/.test(phoneTrim)) newErrors.phone = 'Format invalide. Ex: 01020304';
    if (!checkoutForm.address) newErrors.address = 'Veuillez sélectionner une adresse';
    if (Object.keys(newErrors).length > 0) { setCheckoutErrors(newErrors); return; }

    const codByDefault = catalogue?.settings?.codByDefault !== false;
    try {
      setIsSubmitting(true);
      const res = await fetch(`${API}/v1/public/catalogue/${slug}/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: checkoutForm.customer_name,
          customer_phone: selectedCountryCode + phoneTrim,
          delivery_address: checkoutForm.address,
          delivery_lat: checkoutForm.lat || 0,
          delivery_lng: checkoutForm.lng || 0,
          instructions: checkoutForm.instructions,
          items: checkoutItems.map(toOrderItem),
          ...(catalogue?.settings?.estimationEnabled && estimation?.tariff != null ? { delivery_fee: estimation.tariff } : {}),
          cod: codByDefault,
        }),
      });
      if (!res.ok) {
        // Lire le corps en texte brut d'abord : le serveur peut renvoyer du non-JSON
        // (page d'erreur HTML, texte, 500…), auquel cas res.json() masquerait la vraie cause.
        const rawBody = await res.text().catch(() => '');
        console.error('[Catalogue] Échec de la commande', {
          status: res.status,
          statusText: res.statusText,
          url: res.url,
          body: rawBody,
        });
        let serverMessage = '';
        try { serverMessage = JSON.parse(rawBody)?.message || ''; } catch { /* corps non-JSON */ }
        throw new Error(serverMessage || `Erreur lors de la commande (HTTP ${res.status})`);
      }
      const data = await res.json();
      // Remember this customer's delivery info on their device for next time.
      try {
        localStorage.setItem(PREFILL_STORAGE_KEY, JSON.stringify({
          customer_name: checkoutForm.customer_name,
          countryCode: selectedCountryCode,
          phone: checkoutForm.phone,
          address: checkoutForm.address,
          lat: checkoutForm.lat,
          lng: checkoutForm.lng,
          instructions: checkoutForm.instructions,
        }));
      } catch (err) { /* ignore storage errors (private mode, quota) */ }
      setOrderResult(data);
      setView('success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const deliveryFee = catalogue?.settings?.estimationEnabled && estimation?.tariff != null ? estimation.tariff : 0;
      trackPurchase({
        value: checkoutTotal + deliveryFee,
        orderId: data.trackingID || data.trackingId,
      });
    } catch (e) {
      // Couvre aussi les échecs réseau (serveur down, CORS, connexion refusée) qui ne
      // passent pas par le bloc !res.ok ci-dessus.
      console.error('[Catalogue] Erreur commande (exception)', e);
      alert(e.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── WhatsApp: send the cart back to the conversation ─────────────────────
  const handleWhatsAppSubmit = async () => {
    if (checkoutItems.length === 0) return;
    try {
      setIsSubmitting(true);
      const res = await fetch(`${API}/v1/public/whatsapp/cart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: waToken, items: checkoutItems.map(toOrderItem) }),
      });
      if (res.status === 410) {
        setWhatsapp(prev => ({ ...prev, expired: true }));
        return;
      }
      if (!res.ok) {
        let code = '';
        try { code = (await res.json())?.message || ''; } catch { /* corps non-JSON */ }
        throw new Error(code === 'product_not_found'
          ? "Un produit de votre panier n'est plus disponible. Retirez-le, puis réessayez."
          : 'Une erreur est survenue. Veuillez réessayer.');
      }
      const data = await res.json();
      setWhatsapp(prev => ({ ...prev, url: data.whatsapp_url || prev.url }));
      setCart([]);
      setBuyNowItems(null);
      setView('whatsapp-sent');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      alert(e.message || 'Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Shared derived values ─────────────────────────────────────────────────
  // From WhatsApp, the "order on WhatsApp" button would only lead back to it.
  const settings = waToken ? { ...(catalogue?.settings || {}), whatsappEnabled: false } : (catalogue?.settings || {});
  const businessName = catalogue?.businessName || 'Catalogue';
  const accentColor = settings.primaryColorHex ? `#${settings.primaryColorHex}` : null;
  const displayedProducts = searchResults !== null ? searchResults : products;

  // ─── Location picker overlay ───────────────────────────────────────────────
  if (showLocationPicker) {
    return (
      <div
        className="fixed inset-0 z-[9999] md:bg-black md:bg-opacity-50 md:flex md:items-start md:justify-center md:p-4 md:pt-16"
        onClick={closePicker}
      >
        <div
          className="bg-white w-full h-[100dvh] flex flex-col overflow-hidden md:rounded-2xl md:shadow-2xl md:max-w-2xl md:h-[calc(100dvh-8rem)]"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-xl font-bold text-gray-900">Localisation</h3>
            <button onClick={closePicker} className="text-gray-400 hover:text-gray-600 transition-colors">
              <CloseIcon />
            </button>
          </div>
          <div className="flex-1 relative">
            <OuigooLocationPicker
              apiKey={GOOGLE_MAPS_API_KEY}
              theme="ouigoo"
              primaryColor={accentColor || undefined}
              language="fr"
              country={isoByPrefix[selectedCountryCode]}
              centerOnUserLocation
              enableSatelliteView
              enableMyLocation
              enableSearch
              confirmButtonStyle="rectangle"
              onLocationSelect={handleLocationSelect}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        </div>
      </div>
    );
  }

  // ─── Loading ───────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-700">
          <Spinner />
          <span>Chargement…</span>
        </div>
      </div>
    );
  }

  // ─── Error ─────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center space-y-4">
          <h1 className="text-2xl font-bold text-gray-900">Catalogue introuvable</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // ─── View routing ──────────────────────────────────────────────────────────
  if (waToken && whatsapp.expired) {
    return <WhatsAppExpiredView whatsappUrl={whatsapp.url} />;
  }

  if (view === 'whatsapp-sent') {
    return <WhatsAppSentView whatsappUrl={whatsapp.url} businessName={businessName} />;
  }

  if (view === 'checkout' && waToken) {
    return (
      <WhatsAppCheckoutView
        checkoutItems={checkoutItems}
        checkoutTotal={checkoutTotal}
        checkoutCount={checkoutCount}
        buyNowItems={buyNowItems}
        setBuyNowItems={setBuyNowItems}
        changeQuantity={changeQuantity}
        businessName={businessName}
        isSubmitting={isSubmitting}
        onSubmit={handleWhatsAppSubmit}
        setView={setView}
      />
    );
  }

  if (view === 'success') {
    return <SuccessView orderResult={orderResult} setCart={setCart} setBuyNowItems={setBuyNowItems} setView={setView} setOrderResult={setOrderResult} />;
  }

  if (view === 'checkout') {
    return (
      <CheckoutView
        checkoutItems={checkoutItems}
        checkoutTotal={checkoutTotal}
        checkoutCount={checkoutCount}
        buyNowItems={buyNowItems}
        setBuyNowItems={setBuyNowItems}
        addToCart={addToCart}
        removeFromCart={removeFromCart}
        catalogue={catalogue}
        accentColor={accentColor}
        selectedCountryCode={selectedCountryCode}
        setSelectedCountryCode={setSelectedCountryCode}
        countryOptions={countryOptions}
        checkoutForm={checkoutForm}
        setCheckoutForm={setCheckoutForm}
        checkoutErrors={checkoutErrors}
        setCheckoutErrors={setCheckoutErrors}
        hasPrefill={hasPrefill}
        clearPrefill={clearPrefill}
        isSubmitting={isSubmitting}
        handleSubmitOrder={handleSubmitOrder}
        setView={setView}
        setShowLocationPicker={setShowLocationPicker}
        estimation={estimation}
        isEstimating={isEstimating}
      />
    );
  }

  if (view === 'product' && selectedProduct) {
    return (
      <ProductView
        selectedProduct={selectedProduct}
        slug={slug}
        businessName={businessName}
        accentColor={accentColor}
        settings={settings}
        products={products}
        loadCatalogue={loadCatalogue}
        canGoBack={canGoBack}
        cartEnabled={catalogue?.settings?.cartEnabled !== false}
        cartCount={cartCount}
        addToCart={addToCart}
        productQuantity={productQuantity}
        setProductQuantity={setProductQuantity}
        selectedVariant={selectedVariant}
        setSelectedVariant={setSelectedVariant}
        selectedOptions={selectedOptions}
        setSelectedOptions={setSelectedOptions}
        activeImageIndex={activeImageIndex}
        setActiveImageIndex={setActiveImageIndex}
        setBuyNowItems={setBuyNowItems}
        setView={setView}
        setSelectedProduct={setSelectedProduct}
        setSearchOpen={setSearchOpen}
        setSearchQuery={setSearchQuery}
      />
    );
  }

  return (
    <CatalogueView
      slug={slug}
      businessName={businessName}
      settings={settings}
      accentColor={accentColor}
      displayedProducts={displayedProducts}
      cart={cart}
      cartEnabled={catalogue?.settings?.cartEnabled !== false}
      cartCount={cartCount}
      cartTotal={cartTotal}
      addToCart={addToCart}
      removeFromCart={removeFromCart}
      getCartItem={getCartItem}
      setBuyNowItems={setBuyNowItems}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      searchOpen={searchOpen}
      setSearchOpen={setSearchOpen}
      isSearching={isSearching}
      loadingMore={loadingMore}
      sentinelRef={sentinelRef}
      setView={setView}
      setSelectedProduct={setSelectedProduct}
      setCanGoBack={setCanGoBack}
    />
  );
};

export default CataloguePage;
